import { queryKeyBuilder } from '~/data/utils/helpers/query-key.builder';
import type { PagingParams, SortingParams } from '~/data/utils/types';
import { useMutation, type UseMutationOptions, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { api, apiConfig, dazzler, dazzlerConfiguration, type EndpointResponse } from '~/data/dazzler-api';
import {
  ValidationChecksComparisonsApi,
  ValidationChecksComparisonsApiAxiosParamCreator,
} from '~/data/openapi-client/index';
import ky from 'ky';
import { downloadFile, getFilename } from '~/data/downloads/file';
import type { AxiosError } from 'axios';

export const validationChecksComparisonsKeys = (() => {
  const base = 'validation-checks-comparisons';

  return {
    base: () => queryKeyBuilder(base),
    list: (params?: ValidationChecksComparisonParams) => queryKeyBuilder(base, 'list', params),
  };
})();

export type ValidationChecksComparisonParams = PagingParams;

export function useListValidationChecksComparisons(
  params?: ValidationChecksComparisonParams,
  options?: UseQueryOptions<EndpointResponse<ValidationChecksComparisonsApi['listComparisons']>>,
) {
  return useQuery({
    queryKey: validationChecksComparisonsKeys.list(params),
    queryFn: async function getValidationChecksComparisons() {
      const response = await dazzler.validationChecksComparisons.listComparisons(params);

      return response.data;
    },
    ...options,
  });
}

export function useDownloadValidationChecksComparisonFile(
  options?: UseMutationOptions<void, AxiosError, { comparisonId: string; signal?: AbortSignal }>,
) {
  return useMutation({
    mutationFn: async function downloadComparisonFile({ comparisonId, signal }) {
      const { url } = await ValidationChecksComparisonsApiAxiosParamCreator(
        dazzlerConfiguration,
      ).downloadComparisonFile(comparisonId);
      const endpoint = new URL(url, apiConfig.baseURL);
      const response = await ky(endpoint, {
        headers: {
          Authorization: api.getAuthorizationHeader() ?? '',
        },
        retry: {
          limit: 3,
        },
        signal,
      });

      const blob = await response.blob();
      const fileName = getFilename(response.headers.get('Content-Disposition'), `comparison-${comparisonId}.csv`);

      const file = new File([blob], fileName, { type: 'text/csv' });
      downloadFile(file);
    },
    ...options,
  });
}
